/**
 * Running inside the JFH Forms Teams app (F7). Teams opens the admin site with
 * `?inTeams=1`; from then on (this tab's session) every API call carries a
 * Teams SSO token instead of relying on Cloudflare Access, which can't sign in
 * inside Teams. The API verifies the token itself (api/src/middleware/teams-sso.ts).
 * The Teams library is only loaded when actually inside Teams.
 */

const FLAG = 'jfh-in-teams';

function readFlag(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('inTeams') === '1') {
      sessionStorage.setItem(FLAG, '1');
      return true;
    }
    return sessionStorage.getItem(FLAG) === '1';
  } catch {
    return new URLSearchParams(window.location.search).get('inTeams') === '1';
  }
}

const inside = typeof window !== 'undefined' && readFlag();

/** True inside the JFH Forms Teams app. */
export function inTeams(): boolean {
  return inside;
}

type TeamsJs = typeof import('@microsoft/teams-js');
let sdk: Promise<TeamsJs> | null = null;
function teamsSdk(): Promise<TeamsJs> {
  sdk ??= import('@microsoft/teams-js').then(async (t) => {
    await t.app.initialize();
    return t;
  });
  return sdk;
}

let cached: { token: string; exp: number } | null = null;

/** A Teams SSO token for the API, reused until a minute before it expires. */
export async function teamsToken(fresh = false): Promise<string> {
  if (!fresh && cached && cached.exp - 60_000 > Date.now()) return cached.token;
  const t = await teamsSdk();
  const token = await t.authentication.getAuthToken();
  let exp = Date.now() + 5 * 60_000;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    if (payload.exp) exp = payload.exp * 1000;
  } catch {
    /* keep the short default */
  }
  cached = { token, exp };
  return token;
}

/**
 * fetch() for the API: inside Teams it adds the SSO token, and retries once
 * with a fresh token if the API says the old one is no good.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  if (!inside) return fetch(path, init);
  const send = async (fresh: boolean) => {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${await teamsToken(fresh)}`);
    return fetch(path, { ...init, headers });
  };
  const res = await send(false);
  return res.status === 401 ? send(true) : res;
}

/**
 * Open a page in a new window — inside the Teams desktop app window.open is
 * blocked, so ask Teams to open it in the browser instead.
 */
export function openExternal(url: string): void {
  if (!inside) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  void teamsSdk()
    .then((t) => t.app.openLink(url))
    .catch(() => window.open(url, '_blank', 'noopener'));
}

/**
 * Where a Teams Activity notification wants to land: its deep link carries
 * `subEntityId` "suppliers:<id>" or "customers:<id>" (api/src/lib/teams-notify.ts).
 * Returns the admin path to open, once per notification.
 */
export async function teamsDeepLinkPath(): Promise<string | null> {
  if (!inside) return null;
  try {
    const t = await teamsSdk();
    const ctx = await t.app.getContext();
    const sub = ctx.page?.subPageId ?? '';
    const m = /^(customers|suppliers):([A-Za-z0-9-]+)$/.exec(sub);
    if (!m) return null;
    const seen = 'jfh-teams-deeplink';
    try {
      if (sessionStorage.getItem(seen) === sub) return null; // already opened this one
      sessionStorage.setItem(seen, sub);
    } catch {
      /* no storage: open it anyway */
    }
    return `/${m[1]}?open=${encodeURIComponent(m[2]!)}`;
  } catch {
    return null;
  }
}
